import { z } from 'zod';
import type {
  ConsentListener,
  ConsentPreferences,
  CookiePro,
  CookieProConfiguration,
  CookieProFailure,
} from '../models/services/cookiePro';
import { log } from '../utils/log';

const GROUPS_UPDATED = 'OneTrustGroupsUpdated';
const READINESS_TIMEOUT_MS = 3000;
const EMPTY_GROUPS: readonly string[] = Object.freeze([]);
const DEFAULT_CATEGORIES = Object.freeze({
  necessary: 'C0001',
  performance: 'C0002',
  marketing: 'C0004',
});
const groupIdSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_-]+$/);
const configurationSchema = z
  .object({
    brandScriptId: z.string().trim().min(1),
    src: z.url(),
    scriptId: z
      .string()
      .trim()
      .regex(/^[A-Za-z][A-Za-z0-9_-]*$/)
      .default('cookiePro'),
  })
  .strict();

type NormalizedConfiguration = Readonly<Required<CookieProConfiguration>>;

// Local browser types avoid conflicting with existing tenant Window augmentations.
type CookieProWindow = Window & {
  OneTrust?: {
    ToggleInfoDisplay?: () => void;
    OnConsentChanged?: (callback: () => void) => void;
  };
  OnetrustActiveGroups?: string;
};

const createUnresolvedSnapshot = (status: 'unconfigured' | 'loading'): ConsentPreferences =>
  Object.freeze({
    status,
    source: 'unresolved',
    necessary: true,
    performance: false,
    marketing: false,
    activeGroups: EMPTY_GROUPS,
  });

const summarizePreferences = (preferences: ConsentPreferences) => ({
  status: preferences.status,
  source: preferences.source,
  necessary: preferences.necessary,
  performance: preferences.performance,
  marketing: preferences.marketing,
  activeGroups: preferences.activeGroups,
});

const normalizeConfiguration = (input: CookieProConfiguration): NormalizedConfiguration => {
  const result = configurationSchema.safeParse(input);
  if (!result.success) {
    throw new TypeError(`Invalid CookiePro configuration: ${result.error.message}`);
  }

  const parsed = result.data;
  return Object.freeze({
    ...parsed,
    src: new URL(parsed.src).href,
  });
};

const normalizeGroups = (value: unknown): readonly string[] | undefined => {
  if (!Array.isArray(value) || !value.every((group) => typeof group === 'string')) {
    return undefined;
  }

  const groups = value.map((group: string) => group.trim()).filter(Boolean);
  if (groups.some((group) => !groupIdSchema.safeParse(group).success)) return undefined;
  return Object.freeze([...new Set(groups)].sort());
};

/**
 * Holds this document's CookiePro configuration, current preferences and subscribers.
 * configure() sets the tenant; initialise() loads the provider and waits for preferences.
 * Provider updates notify subscribers; only subsequent consent-change callbacks reload.
 * OneTrust owns saved choices and policy; this service never accepts consent or starts trackers.
 */
class CookieProService implements CookiePro {
  private configuration: NormalizedConfiguration | undefined;
  private snapshot = createUnresolvedSnapshot('unconfigured');
  private initialisationPromise: Promise<ConsentPreferences> | undefined;
  private resolveInitialisation: ((preferences: ConsentPreferences) => void) | undefined;
  private initialisationTimeout: number | undefined;
  private providerScript: HTMLScriptElement | undefined;
  private consentChangeHandlerRegistered = false;
  private reloadRequested = false;
  private readonly listeners = new Set<ConsentListener>();

  configure(input: CookieProConfiguration): void {
    const next = normalizeConfiguration(input);
    if (this.configuration) {
      if (JSON.stringify(this.configuration) !== JSON.stringify(next)) {
        throw new Error('CookiePro is already configured for a different tenant or policy.');
      }
      return;
    }

    this.configuration = next;
  }

  initialise(): Promise<ConsentPreferences> {
    if (!this.configuration) throw new Error('Configure CookiePro before initializing it.');
    if (this.initialisationPromise) return this.initialisationPromise;

    // All callers share the first startup result, while subscriptions keep receiving updates.
    this.initialisationPromise = new Promise<ConsentPreferences>((resolve) => {
      this.resolveInitialisation = resolve;
    });

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      this.fail('browser-unavailable', 'CookiePro requires a browser document.');
      return this.initialisationPromise;
    }

    this.publish(createUnresolvedSnapshot('loading'));
    try {
      this.loadProviderScript(this.configuration);
    } catch (_error) {
      window.removeEventListener(GROUPS_UPDATED, this.handleGroupsUpdated);
      this.fail('script-error', 'The CookiePro script could not be initialized.');
      this.providerScript = undefined;
    }

    return this.initialisationPromise;
  }

  getSnapshot(): ConsentPreferences {
    return this.snapshot;
  }

  subscribe(listener: ConsentListener): () => void {
    if (typeof listener !== 'function') {
      throw new TypeError('A CookiePro subscriber must be a function.');
    }
    // Separate subscriptions stay independent even when given the same callback.
    const observer: ConsentListener = (preferences) => listener(preferences);
    this.listeners.add(observer);
    observer(this.snapshot);
    return () => {
      this.listeners.delete(observer);
    };
  }

  openPreferences(): boolean {
    if (!this.providerScript || typeof window === 'undefined') return false;
    const provider = (window as CookieProWindow).OneTrust;
    if (typeof provider?.ToggleInfoDisplay !== 'function') return false;

    try {
      provider.ToggleInfoDisplay();
      return true;
    } catch (_error) {
      return false;
    }
  }

  private loadProviderScript(config: NormalizedConfiguration): void {
    // scriptId identifies the DOM element; brandScriptId identifies the CookiePro tenant.
    const identifiedElement = document.getElementById(config.scriptId);
    const candidates = new Set(document.querySelectorAll('script[data-domain-script]'));
    if (identifiedElement) candidates.add(identifiedElement);

    const [candidate] = candidates;
    if (
      candidates.size > 1 ||
      (candidate &&
        (!(candidate instanceof HTMLScriptElement) ||
          candidate.getAttribute('data-domain-script') !== config.brandScriptId ||
          candidate.src !== config.src))
    ) {
      this.fail(
        'script-conflict',
        'Another element or CookiePro configuration is already present.',
      );
      return;
    }

    // Never assume an unidentified preloaded provider belongs to this tenant.
    if (!candidate && (window as CookieProWindow).OneTrust) {
      this.fail('script-conflict', 'An existing OneTrust provider has no matching script.');
      return;
    }

    let script = candidate as HTMLScriptElement | undefined;
    const existingScript = Boolean(script);
    if (!script) {
      script = document.createElement('script');
      script.id = config.scriptId;
      script.src = config.src;
      script.async = true;
      script.setAttribute('data-domain-script', config.brandScriptId);
      script.setAttribute('data-document-language', 'true');
    }

    this.providerScript = script;
    // Keep this listener after startup, including timeouts, to receive late preference updates.
    window.addEventListener(GROUPS_UPDATED, this.handleGroupsUpdated);
    script.addEventListener('load', this.handleScriptLoad);
    script.addEventListener('error', this.handleScriptError);
    this.initialisationTimeout = window.setTimeout(
      () =>
        this.fail('timeout', 'OneTrust did not provide preferences before the readiness timeout.'),
      READINESS_TIMEOUT_MS,
    );

    if (existingScript) this.readProviderPreferences();
    else document.head.insertBefore(script, document.head.firstChild);
  }

  // Arrow handlers retain this service instance when the browser invokes them.
  private readonly handleGroupsUpdated = (event: Event): void => {
    const groups = normalizeGroups((event as CustomEvent<unknown>).detail);
    if (!groups) {
      this.fail('invalid-groups', 'OneTrust supplied an invalid active-group list.');
      return;
    }
    this.applyGroups(groups);
  };

  private readonly handleScriptLoad = (): void => {
    // The stub download is not readiness; it may still be fetching the actual CMP.
    this.readProviderPreferences();
  };

  private readonly handleScriptError = (): void => {
    this.fail('script-error', 'The CookiePro script could not be loaded.');
  };

  private registerConsentChangeHandler(): void {
    const provider = (window as CookieProWindow).OneTrust;
    if (this.consentChangeHandlerRegistered || typeof provider?.OnConsentChanged !== 'function') {
      return;
    }

    try {
      provider.OnConsentChanged(this.handleConsentChanged);
      // Ignore any synchronous replay during registration: it is not a subsequent change.
      this.consentChangeHandlerRegistered = true;
    } catch (error) {
      log.error({ message: 'Failed to register CookiePro consent-change handler', data: error });
    }
  }

  private readonly handleConsentChanged = (): void => {
    if (
      !this.consentChangeHandlerRegistered ||
      this.snapshot.status !== 'ready' ||
      this.reloadRequested
    ) {
      return;
    }

    const provider = window as CookieProWindow;
    if (typeof provider.OnetrustActiveGroups !== 'string') return;
    const groups = normalizeGroups(provider.OnetrustActiveGroups.split(','));
    if (!groups?.includes(DEFAULT_CATEGORIES.necessary)) return;

    // Set before publishing so reentrant or duplicate callbacks cannot request another reload.
    this.reloadRequested = true;
    this.applyGroups(groups);
    window.location.reload();
  };

  private readProviderPreferences(): void {
    const provider = window as CookieProWindow;
    if (!provider.OneTrust || typeof provider.OnetrustActiveGroups !== 'string') return;
    const groups = normalizeGroups(provider.OnetrustActiveGroups.split(','));
    // A populated Always Active group distinguishes initialized state from a stub.
    if (groups?.includes(DEFAULT_CATEGORIES.necessary)) this.applyGroups(groups);
  }

  private applyGroups(groups: readonly string[]): void {
    const next: ConsentPreferences = Object.freeze({
      status: 'ready',
      source: 'provider',
      necessary: true,
      performance: groups.includes(DEFAULT_CATEGORIES.performance),
      marketing: groups.includes(DEFAULT_CATEGORIES.marketing),
      activeGroups: groups,
    });
    this.finishInitialisation(next);
    this.publish(next);
    // Register only after real provider preferences arrive, including late timeout recovery.
    this.registerConsentChangeHandler();
  }

  private fail(code: CookieProFailure['code'], message: string): void {
    const next: ConsentPreferences = Object.freeze({
      status: 'error',
      source: 'fallback',
      necessary: true,
      performance: true,
      marketing: true,
      activeGroups: EMPTY_GROUPS,
      error: Object.freeze({ code, message }),
    });
    this.finishInitialisation(next);
    this.publish(next);
  }

  private finishInitialisation(next: ConsentPreferences): void {
    if (!this.resolveInitialisation) return;

    log.debug({
      message: 'CookiePro initial preferences',
      data: summarizePreferences(next),
    });

    if (this.initialisationTimeout !== undefined) {
      window.clearTimeout(this.initialisationTimeout);
      this.initialisationTimeout = undefined;
    }
    this.providerScript?.removeEventListener('load', this.handleScriptLoad);
    this.providerScript?.removeEventListener('error', this.handleScriptError);
    this.resolveInitialisation(next);
    this.resolveInitialisation = undefined;
  }

  private publish(next: ConsentPreferences): void {
    if (JSON.stringify(this.snapshot) === JSON.stringify(next)) return;

    const preferencesChanged = this.snapshot.status === 'ready' && next.status === 'ready';
    if (preferencesChanged) {
      log.debug({
        message: 'CookiePro preferences changed',
        data: {
          from: summarizePreferences(this.snapshot),
          to: summarizePreferences(next),
        },
      });
    }

    this.snapshot = next;
    for (const listener of [...this.listeners]) {
      try {
        listener(next);
      } catch (error) {
        // One faulty observer must not prevent other consumers receiving a denial.
        console.error('CookiePro consent subscriber failed.', error);
      }
    }
  }
}

// Every importer shares this instance. Construction does not access the browser or load scripts.
export const cookiePro: CookiePro = new CookieProService();
